import { ScrollContainer, Select } from '@bloomfieldrobotics/canopy';
import {
  Box,
  Pagination,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { useCallback, useState } from 'react';
import { useTranslation } from '../../../services/translation/hooks/useTranslation';
import { NoDataMessage } from '../components/NoDataMessage';
import { SCANS_PAGE_REFRESH_INTERVAL_MS } from '../scansPageConstants';
import { ScanCard } from './components/ScanCard/ScanCard';
import { ScanListSkeleton } from './components/ScanListSkeleton';
import {
  DEFAULT_SCAN_LIST_PAGE_SIZE,
  useRecentScansPaginated,
} from './hooks/useRecentScansPaginated';
import { useScanCardEventHandlers } from './hooks/useScanCardEventHandlers';

export const ScanList = () => {
  const { t } = useTranslation();
  const [pageSize, setPageSize] = useState<number>(DEFAULT_SCAN_LIST_PAGE_SIZE);
  const smallPageWidth = useMediaQuery('(max-width:1300px)');

  const {
    isLoading,
    isFetchingPage,
    page,
    totalPages,
    displayScans,
    handlePageChange,
  } = useRecentScansPaginated(pageSize, SCANS_PAGE_REFRESH_INTERVAL_MS);

  const { handleOnClick } = useScanCardEventHandlers();

  const handlePageSizeChange = useCallback((value?: number) => {
    if (value) {
      setPageSize(value);
    } else {
      setPageSize(DEFAULT_SCAN_LIST_PAGE_SIZE);
    }
  }, []);

  const paginationSection = (
    <Stack
      direction='row'
      spacing={1}
      sx={{
        justifyContent: 'flex-end',
        alignItems: 'center',
        flexWrap: 'wrap',
        pt: '0.5rem',
      }}
    >
      {!smallPageWidth && <Typography>{t('Scans per page')}</Typography>}
      <Select
        options={[
          { value: 25, label: '25' },
          { value: 50, label: '50' },
          { value: 100, label: '100' },
          { value: 150, label: '150' },
          { value: 200, label: '200' },
        ]}
        value={pageSize}
        onChange={handlePageSizeChange}
        sx={{ m: '0 0.25rem' }}
      />
      <Pagination
        disabled={isLoading}
        page={page}
        count={totalPages}
        onChange={handlePageChange}
        showFirstButton
        showLastButton
        boundaryCount={smallPageWidth ? 1 : 2} // Reduce boundary count on small screens
        size={smallPageWidth ? 'small' : 'medium'} // Use smaller pagination on small screens
        sx={{
          justifyContent: 'flex-end',
          display: 'flex',
          flexWrap: 'wrap',
        }}
      />
    </Stack>
  );

  const scanGridSection = (
    <Box sx={{ width: '100%', height: '100%' }}>
      <Grid container spacing={'0.625rem'} columns={10}>
        {displayScans?.map((scan) => (
          <Grid key={scan.id} size={{ xl: 2, lg: 2, md: 3, sm: 5, xs: 10 }}>
            <ScanCard scan={scan} onClick={handleOnClick} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  let content;
  if (isLoading || isFetchingPage) {
    content = <ScanListSkeleton />;
  } else if (displayScans.length === 0) {
    content = <NoDataMessage message='No scans found' />;
  } else {
    content = scanGridSection;
  }

  return (
    <Stack sx={{ height: '100%', overflow: 'hidden', gap: 1 }}>
      <ScrollContainer>
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {content}
        </Box>
      </ScrollContainer>
      <Box>{paginationSection}</Box>
    </Stack>
  );
};
